import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleMinus,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import {
  CalendarIcon,
  ClockIcon,
  DollarIcon,
  InfoIcon,
  PinIcon,
  TagIcon,
  TrophyIcon,
  UsersIcon,
} from "./BundleIcons";
import {
  OPTION_BUNDLE,
  OPTION_SINGLE,
  formatMoney,
  formatSavings,
  getBundleCombineCount,
  getBundleName,
  getBundlePrice,
  getCombineDate,
  getCombineEventId,
  getCombineLocation,
  getCombineName,
  getCombinePrice,
  getCombineTime,
  getCombines,
  getDrillCombineCount,
  getHasPositionDrills,
  getPositionDrillPrice,
  getSavingsAmount,
  getSavingsPercent,
  getTotalSeparatePrice,
  pluralize,
  resolveSelectedCombine,
} from "./bundleHelpers";

const CombineDetails = ({ combine }) => (
  <div className="text-sm text-gray-600 flex flex-wrap gap-x-5 gap-y-1 mt-1.5">
    {getCombineDate(combine) && (
      <span className="flex items-center gap-2">
        <CalendarIcon />
        {getCombineDate(combine)}
      </span>
    )}
    {getCombineTime(combine) && (
      <span className="flex items-center gap-2">
        <ClockIcon />
        {getCombineTime(combine)}
      </span>
    )}
    {getCombineLocation(combine) && (
      <span className="flex items-center gap-2">
        <PinIcon />
        {getCombineLocation(combine)}
      </span>
    )}
  </div>
);

const IncludedChip = ({ children }) => (
  <span className="inline-flex items-center gap-2 bg-green-50 text-gray-800 text-sm rounded px-2.5 py-1">
    <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 text-green-600" />
    {children}
  </span>
);

const OptionalChip = ({ children }) => (
  <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 text-sm rounded px-2.5 py-1">
    <FontAwesomeIcon icon={faCircleMinus} className="h-4 w-4 text-gray-700" />
    {children}
  </span>
);

const BundleCombineRow = ({ combine, isSelected }) => (
  <div
    data-testid={`bundle-combine-${getCombineEventId(combine)}`}
    className="border border-gray-200 rounded-lg px-4 py-3 bg-white"
  >
    {isSelected && (
      <div className="text-xs font-semibold text-blue-700 uppercase mb-0.5">
        Your Selected Combine (Included)
      </div>
    )}
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
      <div className="min-w-0">
        <div className="font-semibold text-gray-900 break-words">
          {getCombineName(combine)}
        </div>
        <CombineDetails combine={combine} />
      </div>
      <div className="flex flex-col sm:items-end gap-1.5 flex-shrink-0">
        <span className="font-bold text-gray-900">
          {formatMoney(getCombinePrice(combine))}
        </span>
        {getHasPositionDrills(combine) && (
          <IncludedChip>
            Position Drill included (+
            {formatSavings(getPositionDrillPrice(combine))} value)
          </IncludedChip>
        )}
      </div>
    </div>
  </div>
);

// The event options and bundle summary for the returning athlete registration
// modal, in the dashboard's light styling (the new athlete page keeps its dark
// one). Purely presentational: the caller owns the selected option and payment.
const CombineBundleOffer = ({ bundle, eventId, option, onOptionChange }) => {
  const combines = getCombines(bundle);
  const selectedCombine = resolveSelectedCombine(bundle, eventId);
  const remainingCombines = combines.filter(
    (combine) => combine !== selectedCombine,
  );
  const selectedHasDrills = getHasPositionDrills(selectedCombine);
  const drillCombineCount = getDrillCombineCount(bundle);
  const combineCount = getBundleCombineCount(bundle);
  const savingsAmount = getSavingsAmount(bundle);
  const savingsPercent = Math.round(getSavingsPercent(bundle));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 p-5">
      <div className="lg:col-span-3 min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-xl font-bold text-gray-900">
            Choose Your Event Options
          </h2>
          <span className="inline-flex items-center gap-2 bg-red-500 text-white text-xs font-medium uppercase px-3 py-1 rounded">
            <FontAwesomeIcon icon={faStar} className="h-3 w-3" />
            Best Value
          </span>
        </div>
        <p className="text-gray-700 mt-2">
          Choose one option below. Add more combines and save money with a
          bundle.
        </p>

        <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 flex items-center gap-4 mt-4">
          <InfoIcon className="h-8 w-8 text-blue-700" />
          <div>
            <p className="text-gray-900">
              Your current registration is already included in this bundle.
            </p>
            {remainingCombines.length > 0 && (
              <p className="text-sm text-gray-700">
                Add {pluralize(remainingCombines.length, "more combine")}
                {savingsAmount > 0 && (
                  <>
                    {" "}
                    and save{" "}
                    <span className="font-bold">
                      {formatSavings(savingsAmount)}
                    </span>
                  </>
                )}
                .
                {drillCombineCount > 0 &&
                  " All available position drills will be included."}
              </p>
            )}
          </div>
        </div>

        {/* Single-event option */}
        <label
          className={`flex items-start gap-4 cursor-pointer rounded-lg border-2 px-4 py-4 mt-6 ${
            option === OPTION_SINGLE
              ? "border-blue-600 bg-blue-50/40"
              : "border-gray-200 bg-white"
          }`}
        >
          <input
            type="radio"
            name="eventOption"
            value={OPTION_SINGLE}
            checked={option === OPTION_SINGLE}
            onChange={() => onOptionChange(OPTION_SINGLE)}
            aria-label="Single event"
            className="mt-1 h-5 w-5 flex-shrink-0 accent-blue-600 cursor-pointer"
          />
          <div className="flex-1 min-w-0">
            <div className="flex justify-between gap-4">
              <div className="min-w-0">
                <span className="font-semibold text-lg text-gray-900">
                  Single Event
                </span>
                <p className="text-gray-700 mt-1">Register for this combine.</p>
              </div>
              <span className="text-xl font-bold text-gray-900 whitespace-nowrap">
                {formatMoney(getCombinePrice(selectedCombine))}
              </span>
            </div>
            {selectedHasDrills && (
              <div className="mt-2">
                <OptionalChip>
                  Add Position Drill for +
                  {formatSavings(getPositionDrillPrice(selectedCombine))}{" "}
                  (optional)
                </OptionalChip>
              </div>
            )}
          </div>
        </label>

        {/* Bundle option */}
        <div className="border-t border-gray-200 mt-6 pt-4">
          <label className="flex items-start gap-4 cursor-pointer">
            <input
              type="radio"
              name="eventOption"
              value={OPTION_BUNDLE}
              checked={option === OPTION_BUNDLE}
              onChange={() => onOptionChange(OPTION_BUNDLE)}
              aria-label="Combine bundle"
              className="mt-0.5 h-5 w-5 flex-shrink-0 accent-blue-600 cursor-pointer"
            />
            <div className="min-w-0">
              <div className="font-semibold text-blue-700 uppercase">
                Add {pluralize(remainingCombines.length, "More Combine")} To
                Complete Your Bundle
              </div>
              <p className="text-gray-700">
                {drillCombineCount > 0 ? (
                  <>
                    These combines will include the position drills (where
                    available) at{" "}
                    <span className="text-blue-700">no extra cost</span>.
                  </>
                ) : (
                  `Includes ${pluralize(remainingCombines.length, "additional combine")}.`
                )}
              </p>
            </div>
          </label>

          <div
            className={`mt-4 space-y-3 rounded-lg ${
              option === OPTION_BUNDLE ? "ring-2 ring-blue-600 p-2" : ""
            }`}
          >
            {selectedCombine && (
              <BundleCombineRow combine={selectedCombine} isSelected />
            )}
            {remainingCombines.map((combine) => (
              <BundleCombineRow
                key={getCombineEventId(combine)}
                combine={combine}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Summary card */}
      <div className="lg:col-span-2 border border-gray-200 rounded-lg p-5 h-fit">
        <div className="text-center border-b border-gray-200 pb-5">
          <h3 className="text-xl font-bold text-gray-900">
            {getBundleName(bundle)}
          </h3>
          <p className="text-gray-800 mt-1">
            {pluralize(combineCount, "Combine")}
            {drillCombineCount > 0 &&
              ` + ${pluralize(drillCombineCount, "Position Drill")}`}
          </p>
        </div>

        <div className="flex justify-between gap-4 font-bold text-gray-900 border-b border-gray-200 py-4">
          <span>Total if purchased separately</span>
          <span>{formatMoney(getTotalSeparatePrice(bundle))}</span>
        </div>

        <div className="flex justify-between items-center gap-4 mt-6">
          <span className="text-lg font-bold text-gray-900">Bundle Price</span>
          <span className="text-3xl font-bold text-gray-900">
            {formatMoney(getBundlePrice(bundle))}
          </span>
        </div>

        {savingsAmount > 0 && (
          <div className="bg-green-50 rounded-md px-3 py-3 mt-5 flex items-center justify-center gap-3 font-semibold text-gray-900">
            <TagIcon className="h-5 w-5" />
            <span>
              You Save {formatSavings(savingsAmount)}
              {savingsPercent > 0 && ` (${savingsPercent}% Off)`}
            </span>
          </div>
        )}

        <ul className="text-gray-900 space-y-4 border-t border-gray-200 pt-5 mt-6">
          <li className="flex items-center gap-3">
            <TrophyIcon className="h-6 w-6" />
            Maximize your exposure
          </li>
          <li className="flex items-center gap-3">
            <CalendarIcon className="h-6 w-6" />
            Compete in {combineCount} high-level events
          </li>
          {drillCombineCount > 0 && (
            <li className="flex items-center gap-3">
              <DollarIcon className="h-6 w-6" />
              All position drills included in bundle
            </li>
          )}
          <li className="flex items-center gap-3">
            <UsersIcon className="h-6 w-6" />
            Best value for serious athletes
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CombineBundleOffer;
